import { useEffect } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import RoutingPaths from '../constants/routingPaths';

const sectionFromSearch = (search) =>
  new URLSearchParams(search).get(RoutingPaths.SECTION_QUERY) || '';

const sectionFromPathname = (pathname) => pathname.replace(/^\/+/, '');

/**
 * Build a shareable viewer URL while keeping the SHL in the fragment.
 * @param {object} options URL options.
 * @param {string} [options.section] Section to include in the query string.
 * @param {string} [options.language] Language to include in the query string.
 * @returns {string} The viewer URL.
 */
export const getViewerUrl = ({ section, language } = {}) => {
  const params = new URLSearchParams(window.location.search);
  if (language) {
    params.set('lang', language);
  }
  if (section) {
    params.set(RoutingPaths.SECTION_QUERY, section);
  } else {
    params.delete(RoutingPaths.SECTION_QUERY);
  }
  const query = params.toString();
  return `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
};

/**
 * The in-memory router's starting location, seeded from the ?section= query so
 * a deep link opens directly on the shared section.
 * @returns {string[]} the MemoryRouter initialEntries.
 */
export const getInitialEntries = () => {
  const section = sectionFromSearch(window.location.search);
  return [section ? `/${section}` : '/'];
};

/**
 * Two-way sync between the in-memory router location and the real URL's
 * ?section= query. Push vs. replace mirrors the router's own navigation type,
 * so history behaves exactly as it did with the previous path-based router.
 */
export const useSectionUrlSync = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();

  useEffect(() => {
    const section = sectionFromPathname(location.pathname);
    if (section === sectionFromSearch(window.location.search)) {
      return;
    }
    const language = new URLSearchParams(location.search).get('lang');
    const url = getViewerUrl({ section, language });
    if (navigationType === 'REPLACE') {
      window.history.replaceState(window.history.state, '', url);
    } else {
      window.history.pushState(window.history.state, '', url);
    }
  }, [location.key]);

  // Browser Back/Forward -> drive the in-memory router to match the URL.
  useEffect(() => {
    const handlePopState = () => {
      const section = sectionFromSearch(window.location.search);
      navigate(section ? `/${section}` : '/');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate]);
};
